import React, { useContext } from 'react';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { withFormik } from 'formik';
import { withRouter } from 'react-router-dom';
import * as Yup from 'yup';
import { injectIntl } from 'react-intl';
import { requestAsync, deleteCouponCode, applyCouponCode } from '../../queries/actions';
import { ApplyCouponCodeRequest } from '../../queries/coupons';
import CouponForm from '../../components/CouponForm';
import { translate } from '../../components/Translate';
import { selectCurrentContentPpvId } from '../../queries/selectors/contents';
import { selectClient, selectSearchObj } from '../../global-selectors';
import { pickPromoSlugFromSearchObj } from '../../utils';
import { selectCouponApply, selectExchangeRate } from '../../queries/selectors/bootstrap';
import { selectUserData } from '../../queries/selectors/user';
import { selectCurrentPaymentMethod } from '../../queries/selectors/payment';
import { selectShoppingCartConfig } from '../../queries/selectors/shoppingCart';
import { selectProducts } from '../../queries/selectors/products';
import { dismissCouponError } from '../../queries/actions';
import withCaptcha from '../withCaptcha';
import { MotorplayContext } from '../../context/Context';
import { ProductType } from '../../enums';

const discountCodeValidations = ({ intl }) => Yup.object().shape({
  discountCode: Yup.string()
    .min(1, translate(intl, 'CouponForm.form.discountCode.min'))
    .max(17, translate(intl, 'CouponForm.form.discountCode.max'))
    .trim(translate(intl, 'CouponForm.form.discountCode.trim'))
    .strict(true)
    .required(translate(intl, 'CouponForm.form.discountCode.required'))
});

const withFormikEnhancement = withFormik({
  // Initial state
  mapPropsToStatus: () => ({}),
  mapPropsToValues: () => ({
    discountCode: '',
  }),
  validationSchema: discountCodeValidations,
  handleSubmit: async (values, { setStatus, setSubmitting, props }) => {
    try {
      setSubmitting(true);
      let captchaToken = props.captchaToken;
      if (props.isCaptchaEnabled) {
        if (!captchaToken) {
          captchaToken = await props.executeCaptcha();
        }
        if (!captchaToken) {
          return;
        }
      }
      const response = await props.ApplyCouponCodeRequest({
        code: values.discountCode.toUpperCase(),
        client: props.client,
        shoppingCart: props.shoppingCart,
        products: props.products,
        token: props.user.token,
        user: props.user,
        exchangeRate: props.exchangeRate,
        ...(props.isCaptchaEnabled && { captchaToken }),
        ...(props.promoSlug && { promoSlug: props.promoSlug })
      })
      if ((response && response.status < 200) || (response && response.status >= 300)) {
        if (props.isCaptchaEnabled) props.resetCaptcha();
        return setStatus({
          apiErrorCode: translate(props.intl, `ERRORS.${response.body && response.body.code}.title`, {
            defaultMessage: translate(props.intl, 'ERRORS.SYS-ERR.title')
          })
        });
      }
      if (response?.transformed?.couponApplyRejected) {
        if (props.isCaptchaEnabled) props.resetCaptcha();
        return setStatus({
          apiErrorCode: translate(props.intl, `ERRORS.${response.transformed.couponApplyRejected.code}.title`, {
            defaultMessage: translate(props.intl, 'ERRORS.SYS-ERR.title')
          })
        });
      }
      applyCouponCode(values.discountCode);
      props.resetCaptcha();

      const toBuy =
        response?.transformed?.shoppingCart?.productsToBuy ||
        response?.entities?.shoppingCart?.productsToBuy;
      if (props.setPlanToBuyMotorplay && Array.isArray(toBuy) && toBuy.length) {
        const planLine =
          toBuy.find((p) => p.productType === ProductType.PLAN) || toBuy[0];
        if (planLine?._id) {
          props.setPlanToBuyMotorplay(planLine._id);
        }
      }
    } finally {
      setSubmitting(false);
    }
  },
});

const mapStateToProps = (state,ownProps) => ({
  ppvId: selectCurrentContentPpvId(state),
  shoppingCart: selectShoppingCartConfig(state),
  products: selectProducts(state),
  client: selectClient(state),
  user: selectUserData(state),
  couponApply: selectCouponApply(state),
  exchangeRate: selectExchangeRate(state),
  paymentMethod: selectCurrentPaymentMethod(state),
  couponVerifyError: ownProps.couponVerifyError,
  promoSlug: pickPromoSlugFromSearchObj(selectSearchObj(state)),
});

const mapDispatchToProps = (
  (dispatch) => bindActionCreators({
    ApplyCouponCodeRequest: (data) => requestAsync(ApplyCouponCodeRequest, data),
    deleteCouponCode: (data) => deleteCouponCode(data),
    applyCouponCode: (data) => applyCouponCode(data),
    dismissCouponError: () => dispatch(dismissCouponError())
  }, dispatch)
);

const CouponFormConnected = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withCaptcha,
  withRouter,
  withFormikEnhancement
)(CouponForm);

const CouponFormContainer = (props) => {
  const { setPlanToBuy } = useContext(MotorplayContext);
  return (
    <CouponFormConnected
      {...props}
      setPlanToBuyMotorplay={typeof setPlanToBuy === 'function' ? setPlanToBuy : undefined}
    />
  );
};

export default CouponFormContainer;
